import { useEffect, useRef } from "react";

import { useLang } from "../lib/i18n";
import { appCopy } from "../lib/i18n/app";

type Props = {
  kcal: number;
  onCancel: () => void;
  onConfirm: () => void;
};

// DESIGN.md sec 12: required modal before persisting a target < 1200 kcal.
// Tone matches "Sprawdźmy razem" — no alarm, no shame, with help link.
// Native <dialog> gives focus trap + ESC handling for free.
export function LowTargetSanityModal({ kcal, onCancel, onConfirm }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!node.open) node.showModal();
    return () => {
      if (node.open) node.close();
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onCancelNative = (e: Event) => {
      e.preventDefault();
      onCancel();
    };
    node.addEventListener("cancel", onCancelNative);
    return () => node.removeEventListener("cancel", onCancelNative);
  }, [onCancel]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="low-target-title"
      className="bg-[var(--surface)] rounded-xl p-5 w-full max-w-md backdrop:bg-black/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex flex-col gap-4">
        <h2
          id="low-target-title"
          className="text-[length:var(--text-h3)] font-bold tracking-[-0.02em] text-[var(--ink)]"
        >
          {t.sanityTitle}
        </h2>
        <p className="text-body text-[var(--ink-2)]">{t.sanityBody(kcal)}</p>
        <p className="text-body-sm text-[var(--muted)]">
          {t.sanitySupport}{" "}
          <a href={t.helplineHref} className="text-[var(--accent)] underline font-medium">
            {t.helplineLabel}
          </a>
          .
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="h-11 px-4 bg-[var(--accent)] text-[var(--accent-ink)] rounded-md font-medium transition-opacity hover:opacity-90"
          >
            {t.sanityConfirm}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-11 px-4 text-[var(--ink-2)] rounded-md transition-colors hover:bg-[var(--surface-2)]"
          >
            {t.sanityCancel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
